import type { Json, JsonObject } from './database.types';

export function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function asJsonObject(value: unknown): JsonObject {
  return isJsonObject(value) ? value : {};
}

export function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Converts arbitrary values coming from untyped code (the legacy UI) into valid JSON:
 * drops functions/undefined/symbols, turns non-finite numbers into null and Dates into
 * ISO strings — exactly what JSON.stringify would send over the wire.
 */
export function toJson(value: unknown): Json {
  const encoded: string | undefined = JSON.stringify(value);
  if (encoded === undefined) return null;
  const decoded: unknown = JSON.parse(encoded);
  return decoded as Json;
}

export function toJsonObject(value: unknown): JsonObject {
  const json = toJson(value);
  return isJsonObject(json) ? json : {};
}

/** Deep structural equality for JSON values (key order independent). */
export function jsonEqual(a: Json | undefined, b: Json | undefined): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => jsonEqual(item, b[index]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a).filter((key) => a[key] !== undefined);
    const keysB = Object.keys(b).filter((key) => b[key] !== undefined);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => jsonEqual(a[key], b[key]));
  }
  return false;
}
